import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Upload,
  message,
  Button,
  Card,
  Typography,
  Space,
  Divider,
} from "antd";
import type { UploadFile } from "antd";
import { UploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { WizardStep } from "../WizardStep";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { supabase } from "../../../lib/supabase";
import { useTranslation } from "react-i18next";
import { useCompany } from "../../../hooks/useCompany";
import {
  uploadCommercialInvoiceWithCompanyPath,
  getCommercialInvoicePublicUrl,
  createCommercialInvoiceSignedUrl,
} from "../../../utils/commercialInvoice";
import { enqueueBackgroundCommercialInvoiceAnalysis } from "../../../features/commercialInvoiceAnalysis/commercialInvoiceAnalysisService";
import {
  uploadCustomerPackingSlip,
  createCustomerPackingSlipSignedUrl,
  getCustomerPackingSlipPublicUrl,
} from "../../../utils/customerPackingSlipUtils";
import {
  deleteCustomerPackingSlip,
  getCustomerPackingSlips,
  upsertCustomerPackingSlip,
} from "../../../services/customerPackingSlipService";

interface FormValues {
  reference: string;
  commercial_invoice?: {
    fileList: {
      originFileObj: File;
    }[];
  };
  customer_packing_slip?: {
    fileList: {
      originFileObj: File;
    }[];
  };
}

export const BasicDetails = () => {
  const { state, updateStep, dispatch } = useBookingWizard();
  const [form] = Form.useForm<FormValues>();
  const { t } = useTranslation();
  const { companyDetails } = useCompany();
  const [packingSlipFileList, setPackingSlipFileList] = useState<UploadFile[]>(
    []
  );
  const [currentPackingSlipPath, setCurrentPackingSlipPath] = useState<
    string | null
  >(state.customer_packing_slip_file || null);
  // Fetch existing customer packing slips when component loads
  useEffect(() => {
    const fetchExistingPackingSlips = async () => {
      if (state.id && !state.customer_packing_slip_file) {
        try {
          const result = await getCustomerPackingSlips(state.id);
          if (result.success && result.data && result.data.length > 0) {
            // Update the wizard state with the latest packing slip if any
            const latestPackingSlip = result.data[result.data.length - 1];
            const packingSlipPath = latestPackingSlip.booking_packing_slip_file;

            dispatch({
              type: "UPDATE_STEP",
              payload: {
                step: state.currentStep,
                data: {
                  customer_packing_slip_file: packingSlipPath,
                },
              },
            });
          }
        } catch (error) {
          console.error("Error fetching existing packing slips:", error);
        }
      }
    };

    fetchExistingPackingSlips();
  }, [state.id, state.currentStep, state.customer_packing_slip_file, dispatch]);

  // Sync file list from wizard state (load / remove / return to step)
  useEffect(() => {
    if (state.customer_packing_slip_file) {
      setPackingSlipFileList([
        {
          uid: "-2",
          name:
            state.customer_packing_slip_file.split("/").pop() ||
            "packing-slip.pdf",
          status: "done",
          url: getPackingSlipPublicUrl(state.customer_packing_slip_file),
        },
      ]);
      setCurrentPackingSlipPath(state.customer_packing_slip_file);
    } else {
      setPackingSlipFileList([]);
      setCurrentPackingSlipPath(null);
    }
  }, [state.customer_packing_slip_file]);

  const removePackingSlipRecords = async (): Promise<boolean> => {
    if (!state.id) return true;

    try {
      const result = await getCustomerPackingSlips(state.id);
      if (!result.success || !result.data?.length) return true;

      await Promise.all(
        result.data.map((slip) =>
          deleteCustomerPackingSlip(slip.id, slip.booking_packing_slip_file)
        )
      );
      return true;
    } catch (error) {
      console.error("Error removing packing slip:", error);
      message.error(t("bookingWizard.basicDetails.removePackingSlipFailed"));
      return false;
    }
  };

  const handlePackingSlipRemove = async (): Promise<boolean> => {
    const previousPath = state.customer_packing_slip_file;

    setPackingSlipFileList([]);
    setCurrentPackingSlipPath(null);
    form.setFieldsValue({ customer_packing_slip: { fileList: [] } });
    dispatch({
      type: "UPDATE_STEP",
      payload: {
        step: state.currentStep,
        data: { customer_packing_slip_file: undefined },
      },
    });

    const removed = await removePackingSlipRecords();
    if (!removed) {
      dispatch({
        type: "UPDATE_STEP",
        payload: {
          step: state.currentStep,
          data: { customer_packing_slip_file: previousPath },
        },
      });
      return false;
    }
    return true;
  };

  const handleFileUpload = async (file: File): Promise<string> => {
    try {
      // Get user and booking info
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) throw new Error("User not authenticated");
      if (!state.id) throw new Error("Booking ID not found");

      // Check if we have a company ID (new structure)
      if (companyDetails?.id) {
        console.log(
          "Using company-based path structure for commercial invoice"
        );

        // Use the new utility function to upload with company path
        const companyPath = await uploadCommercialInvoiceWithCompanyPath(
          file,
          companyDetails.id,
          state.id
        );

        if (companyPath) {
          return companyPath;
        } else {
          console.warn(
            "Company path upload failed, falling back to user-based path"
          );
        }
      }

      // Fall back to the legacy user-based path if company upload fails or companyDetails is not available
      console.log(
        "Using legacy user-based path structure for commercial invoice"
      );

      // Sanitize the filename to remove special characters and spaces
      const sanitizedFileName = file.name
        .replace(/\s+/g, "_") // Replace spaces with underscores
        .replace(/[^\w.-]/g, "") // Remove any non-alphanumeric characters except dots and hyphens
        .normalize("NFD") // Normalize accented characters
        .replace(/[\u0300-\u036f]/g, ""); // Remove diacritics

      // Create a clean path structure with user_id (legacy format)
      const fileName = `${user.id}/${
        state.id
      }/${Date.now()}_${sanitizedFileName}`;

      console.log("Uploading file with path:", fileName);

      const { error: uploadError, data } = await supabase.storage
        .from("commercial_invoices")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        throw uploadError;
      }

      console.log("File uploaded successfully:", data.path);
      return data.path;
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  };

  const handlePackingSlipUpload = async (file: File): Promise<string> => {
    try {
      // Get user and booking info
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) throw new Error("User not authenticated");
      if (!state.id) throw new Error("Booking ID not found");

      // Use company-based structure for customer packing slip
      const companyId = companyDetails?.id || 0;
      console.log(
        "Uploading customer packing slip with company ID:",
        companyId
      );

      const uploadResult = await uploadCustomerPackingSlip(
        state.id,
        file,
        companyId
      );

      if (!uploadResult || uploadResult.error) {
        throw new Error(
          `Upload failed: ${uploadResult?.error || "Unknown error"}`
        );
      }

      console.log(
        "Customer packing slip uploaded successfully:",
        uploadResult.path
      );
      return uploadResult.path || "";
    } catch (error) {
      console.error("Customer packing slip upload error:", error);
      throw error;
    }
  };

  const handleSubmit = async (): Promise<void> => {
    try {
      console.log("BasicDetails - Starting form submission");
      console.log("BasicDetails - Current state:", {
        currentStep: state.currentStep,
        bookingId: state.id,
        hasCommercialInvoice: !!state.commercial_invoice_file,
        hasPackingSlip: !!state.customer_packing_slip_file,
      });

      const values = await form.validateFields();
      console.log("BasicDetails - Form validation passed, values:", values);

      let filePath = state.commercial_invoice_file;
      let packingSlipPath: string | undefined = state.customer_packing_slip_file;
      let uploadedNewCommercialInvoice = false;
      const hasPackingSlipSelected = packingSlipFileList.length > 0;

      // Handle commercial invoice upload
      if (values.commercial_invoice?.fileList?.[0]?.originFileObj) {
        try {
          filePath = await handleFileUpload(
            values.commercial_invoice.fileList[0].originFileObj
          );
          uploadedNewCommercialInvoice = true;
        } catch (error: unknown) {
          const uploadError = error as Error;
          console.error("Commercial invoice upload failed:", uploadError);
          message.error(
            "Failed to upload commercial invoice: " +
              (uploadError.message || "Unknown error")
          );
          // Continue with form submission even if file upload fails
        }
      }

      if (!hasPackingSlipSelected) {
        packingSlipPath = undefined;
        await removePackingSlipRecords();
      }

      // Handle customer packing slip upload
      if (values.customer_packing_slip?.fileList?.[0]?.originFileObj) {
        try {
          packingSlipPath = await handlePackingSlipUpload(
            values.customer_packing_slip.fileList[0].originFileObj
          );

          // Update local file list with new uploaded file path
          setPackingSlipFileList([
            {
              uid: "-2",
              name: packingSlipPath.split("/").pop() || "packing-slip.pdf",
              status: "done",
              url: getPackingSlipPublicUrl(packingSlipPath),
            },
          ]);
          setCurrentPackingSlipPath(packingSlipPath);
          console.log(
            "Upload success - Updated currentPackingSlipPath to:",
            packingSlipPath
          );
        } catch (error: unknown) {
          const uploadError = error as Error;
          console.error("Packing slip upload failed:", uploadError);
          message.error(
            "Failed to upload packing slip: " +
              (uploadError.message || "Unknown error")
          );
          // Continue with form submission even if packing slip upload fails
        }
      }

      // Create/update database record for packing slip (prevents duplicates)
      if (packingSlipPath && state.id) {
        try {
          const upsertResult = await upsertCustomerPackingSlip(
            state.id,
            packingSlipPath
          );

          if (!upsertResult.success) {
            console.error(
              "Failed to save packing slip database record:",
              upsertResult.error
            );
            message.warning("Failed to save packing slip database record");
          } else {
            console.log(
              `Packing slip record ${upsertResult.action} successfully`
            );
          }
        } catch (error: unknown) {
          console.error("Error handling packing slip database record:", error);
          // Don't block progression for database record issues
        }
      }

      // Ensure we have the correct packing slip path from database
      if (!packingSlipPath && state.id) {
        try {
          const result = await getCustomerPackingSlips(state.id);
          if (result.success && result.data.length > 0) {
            packingSlipPath = result.data[0].booking_packing_slip_file;
            console.log(
              "Retrieved existing packing slip path from database:",
              packingSlipPath
            );
          }
        } catch (error) {
          console.error("Error fetching existing packing slip:", error);
        }
      }

      console.log(
        "BasicDetails - Final packingSlipPath before updateStep:",
        packingSlipPath
      );
      console.log("BasicDetails - About to call updateStep(4) with data:", {
        reference: values.reference,
        commercial_invoice_file: filePath,
        customer_packing_slip_file: packingSlipPath,
      });

      await updateStep(4, {
        reference: values.reference,
        commercial_invoice_file: filePath,
        customer_packing_slip_file: packingSlipPath,
      });

      if (uploadedNewCommercialInvoice && filePath && state.id) {
        enqueueBackgroundCommercialInvoiceAnalysis(state.id, filePath);
      }

      console.log("BasicDetails - updateStep(4) completed successfully");

      // Note: We rely on the useEffect to sync state when returning to this step
      // The database is the source of truth for packing slips

      console.log("BasicDetails - Form submission completed successfully");
    } catch (error) {
      console.error("Form submission failed:", error);
      message.error("Failed to save details");
    }
  };

  const getPublicUrl = (path: string) => {
    // Try the new utility function which supports both formats
    const publicUrl = getCommercialInvoicePublicUrl(path, true);
    if (publicUrl) {
      return publicUrl;
    }

    // Fallback to direct method if utility function fails
    const { data } = supabase.storage
      .from("commercial_invoices")
      .getPublicUrl(path);
    return data.publicUrl;
  };

  const getSignedUrl = async (path: string) => {
    // Try the new utility function which supports both formats
    const signedUrl = await createCommercialInvoiceSignedUrl(
      path,
      60,
      true,
      state.id
    );
    if (signedUrl) {
      return signedUrl;
    }

    const cleanPath = path.trim().replace(/^\//, "");
    const { data: blob, error } = await supabase.storage
      .from("commercial_invoices")
      .download(cleanPath);

    if (error || !blob) {
      message.error("Failed to generate download link");
      return null;
    }

    return URL.createObjectURL(blob);
  };

  const getPackingSlipPublicUrl = (path: string) => {
    // Try the new utility function
    const publicUrl = getCustomerPackingSlipPublicUrl(path, true);
    if (publicUrl) {
      return publicUrl;
    }

    // Fallback to direct method if utility function fails
    const { data } = supabase.storage
      .from("customerpackingslips")
      .getPublicUrl(path);
    return data.publicUrl;
  };

  const getPackingSlipSignedUrl = async (path: string) => {
    // Try the new utility function
    const signedUrl = await createCustomerPackingSlipSignedUrl(path, {
      expiresIn: 60,
      logError: true,
      bookingId: state.id,
    });
    if (signedUrl) {
      return signedUrl;
    }

    const cleanPath = path.trim().replace(/^\//, "");
    const { data: blob, error } = await supabase.storage
      .from("customerpackingslips")
      .download(cleanPath);

    if (error || !blob) {
      message.error("Failed to generate packing slip download link");
      return null;
    }

    return URL.createObjectURL(blob);
  };

  return (
    <WizardStep
      onNext={handleSubmit}
      onPrevious={() => updateStep(state.currentStep - 1, {})}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          reference: state.reference,
          commercial_invoice: state.commercial_invoice_file
            ? [
                {
                  uid: "-1",
                  name: state.commercial_invoice_file.split("/").pop(),
                  status: "done",
                  url: getPublicUrl(state.commercial_invoice_file),
                },
              ]
            : [],
          customer_packing_slip: state.customer_packing_slip_file
            ? [
                {
                  uid: "-2",
                  name: state.customer_packing_slip_file.split("/").pop(),
                  status: "done",
                  url: getPackingSlipPublicUrl(
                    state.customer_packing_slip_file
                  ),
                },
              ]
            : [],
        }}
      >
        <Form.Item
          label={t("bookingWizard.basicDetails.reference")}
          name="reference"
          tooltip={t("bookingWizard.basicDetails.referenceTooltip")}
          rules={[{ required: false }]}
        >
          <Input placeholder={t("bookingWizard.basicDetails.reference")} />
        </Form.Item>

        <Form.Item
          label={t("bookingWizard.basicDetails.commercialInvoice")}
          name="commercial_invoice"
          rules={[
            {
              required: false,
              message: t(
                "bookingWizard.basicDetails.commercialInvoiceRequired"
              ),
            },
          ]}
        >
          <Upload
            maxCount={1}
            beforeUpload={() => false}
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            defaultFileList={
              state.commercial_invoice_file
                ? [
                    {
                      uid: "-1",
                      name:
                        state.commercial_invoice_file.split("/").pop() || "",
                      status: "done",
                      url: getPublicUrl(state.commercial_invoice_file),
                    },
                  ]
                : []
            }
            onPreview={async () => {
              if (state.commercial_invoice_file) {
                const signedUrl = await getSignedUrl(
                  state.commercial_invoice_file
                );
                if (signedUrl) {
                  window.open(signedUrl, "_blank");
                }
              }
            }}
          >
            <Button icon={<UploadOutlined />}>
              {state.commercial_invoice_file
                ? t("bookingWizard.basicDetails.replaceInvoice")
                : t("bookingWizard.basicDetails.uploadInvoice")}
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item
          label={t("bookingWizard.basicDetails.customerPackingSlip")}
          name="customer_packing_slip"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Upload
            maxCount={1}
            beforeUpload={() => false}
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            fileList={packingSlipFileList}
            onChange={(info) => {
              setPackingSlipFileList(info.fileList);
              form.setFieldsValue({
                customer_packing_slip: info,
              });
            }}
            onRemove={handlePackingSlipRemove}
            onPreview={async () => {
              const filePath =
                currentPackingSlipPath || state.customer_packing_slip_file;
              console.log("Preview clicked - Debug info:", {
                currentPackingSlipPath,
                stateCustomerPackingSlipFile: state.customer_packing_slip_file,
                finalFilePath: filePath,
                packingSlipFileListLength: packingSlipFileList.length,
              });
              if (filePath) {
                console.log("Getting signed URL for path:", filePath);
                const signedUrl = await getPackingSlipSignedUrl(filePath);
                console.log("Generated signed URL:", signedUrl);
                if (signedUrl) {
                  window.open(signedUrl, "_blank");
                }
              }
            }}
          >
            <Button icon={<UploadOutlined />}>
              {packingSlipFileList.length > 0
                ? t("bookingWizard.basicDetails.replacePackingSlip")
                : t("bookingWizard.basicDetails.uploadPackingSlip")}
            </Button>
          </Upload>
        </Form.Item>

        <Divider />

        <Card
          title={
            <Space>
              <InfoCircleOutlined />
              <Typography.Text strong>
                {t("bookingWizard.basicDetails.invoiceChecklist")}
              </Typography.Text>
            </Space>
          }
          size="small"
          style={{ marginBottom: 24 }}
        >
          <Typography.Paragraph>
            {t("bookingWizard.basicDetails.invoiceChecklistInfo")}
          </Typography.Paragraph>

          <ul
            style={{
              paddingLeft: 20,
              marginBottom: 0,
              listStyleType: "disc",
              lineHeight: "1.8em",
            }}
          >
            <li>{t("bookingWizard.basicDetails.checklistSellerInfo")}</li>
            <li>{t("bookingWizard.basicDetails.checklistBuyerInfo")}</li>
            <li>{t("bookingWizard.basicDetails.checklistInvoiceDetails")}</li>
            <li>{t("bookingWizard.basicDetails.checklistProductDetails")}</li>
            <li>{t("bookingWizard.basicDetails.checklistHsCode")}</li>
            <li>{t("bookingWizard.basicDetails.checklistCountryOrigin")}</li>
            <li>{t("bookingWizard.basicDetails.checklistQuantity")}</li>
            <li>{t("bookingWizard.basicDetails.checklistValue")}</li>
            <li>{t("bookingWizard.basicDetails.checklistWeight")}</li>
            <li>{t("bookingWizard.basicDetails.checklistIncoterms")}</li>
          </ul>
        </Card>
      </Form>
    </WizardStep>
  );
};
